import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../hooks/useSettings';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ChangePasswordModal from './ChangePasswordModal';
import EditProfileModal from './EditProfileModal';
import BuyCreditsModal from './BuyCreditsModal';
import UserProfileDropdown from './UserProfileDropdown';
import { CheckCircle2, AlertTriangle, Calendar, DollarSign, Receipt, RefreshCw, Copy, Hash, Plus, ArrowLeft } from 'lucide-react';

const MyOrdersPage = ({ embedded = false }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { settings, loading: settingsLoading, settingsLoaded, safeSettings } = useSettings();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // Header-related state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Hydrate impersonated user into AuthContext for this tab (direct visit or refresh)
  useEffect(() => {
    const sessionUserData = sessionStorage.getItem('loginAsUserData');
    if (sessionUserData) {
      try {
        const parsed = JSON.parse(sessionUserData);
        if (parsed && typeof parsed === 'object') {
          updateUser(parsed);
        }
      } catch (e) {
        console.warn('Failed to parse loginAs userData in MyOrdersPage');
      }
    }
  }, [updateUser]);

  const authHeader = () => {
    // Support login-as sessions (sessionStorage) and normal user sessions (localStorage)
    const loginAsToken = sessionStorage.getItem('loginAsToken');
    const token = loginAsToken || localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const getTimeZone = () => {
    // Prefer configured timezone (e.g., "Asia/Kolkata"); fallback to UTC
    return (safeSettings && safeSettings.timezone) || (settings && settings.timezone) || 'UTC';
  };

  const formatDateTimeTZ = (dateString) => {
    if (!dateString) return '';
    const tz = getTimeZone();
    const d = new Date(dateString);
    // Format: dd/mm/yyyy HH:mm
    const day = new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: '2-digit' }).format(d);
    const month = new Intl.DateTimeFormat('en-GB', { timeZone: tz, month: '2-digit' }).format(d);
    const year = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric' }).format(d);
    const time = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    return `${day}/${month}/${year} ${time}`;
  };

  const formatDateOnlyTZ = (dateString) => {
    if (!dateString) return '';
    const tz = getTimeZone();
    const d = new Date(dateString);
    // dd/mm/yyyy only
    const day = new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: '2-digit' }).format(d);
    const month = new Intl.DateTimeFormat('en-GB', { timeZone: tz, month: '2-digit' }).format(d);
    const year = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric' }).format(d);
    return `${day}/${month}/${year}`;
  };

  const formatPrice = (price, currency = 'INR') => {
    if (currency === 'INR') {
      return `₹${Number(price || 0).toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price || 0);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE_URL}/api/orders/user`, { headers: { ...authHeader() } });
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Layout wrapper
  return (
    <div style={{ backgroundColor: embedded ? 'transparent' : 'var(--color-bg-app)' }}>
      {/* Embedded mode: no header/breadcrumb/refresh; Full page mode uses layout container only */}
      <main style={{ maxWidth: embedded ? '100%' : '1000px', margin: embedded ? '0' : '0 auto', padding: embedded ? '0' : '24px' }}>
        {!embedded && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>My Orders</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outline" onClick={fetchOrders}>
                <RefreshCw size={16} style={{ marginRight: 6 }} />
                Refresh
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
            <RefreshCw size={36} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          </div>
        ) : error ? (
          <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
            <Card style={{ padding: 32, textAlign: 'center' }}>
              <AlertTriangle size={40} color="#ef4444" style={{ marginBottom: 12 }} />
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Error</div>
              <div style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>{error}</div>
              <Button onClick={fetchOrders}>
                <RefreshCw size={16} style={{ marginRight: 6 }} />
                Try again
              </Button>
            </Card>
          </div>
        ) : (
          <>
            {orders.length === 0 ? (
              <Card style={{ padding: 32, textAlign: 'center' }}>
                <div style={{ color: 'var(--color-text-muted)' }}>No orders found.</div>
              </Card>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {orders.map((order) => (
                  <Card key={order.id} style={{ padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, fontSize: 16 }}>{order.plan_name}</div>
                          <div style={{ fontSize: 12, padding: '3px 10px', borderRadius: 14, backgroundColor: 'var(--color-bg-inset)', color: 'var(--color-text-secondary)' }}>
                            {order.plan_type}
                          </div>
                          <div style={{ fontSize: 12, padding: '3px 10px', borderRadius: 14, backgroundColor: '#eef2ff', color: '#4f46e5' }}>
                            {order.status}
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <DollarSign size={16} color="#10b981" />
                            <div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-success)' }}>{formatPrice(order.amount, order.currency)}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Amount</div>
                            </div>
                          </div>

                          {typeof order.credits_granted !== 'undefined' && order.credits_granted !== null && (
                            <div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: '#4f46e5' }}>{Number(order.credits_granted).toLocaleString('en-IN')}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Credits Granted</div>
                            </div>
                          )}

                          <div>
                            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{formatDateOnlyTZ(order.created_at)}</div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Created (dd/mm/yyyy)</div>
                          </div>

                          {order.updated_at && (
                            <div>
                              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{formatDateTimeTZ(order.updated_at)}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Last Updated (local time)</div>
                            </div>
                          )}

                          {order.assigned_at && (
                            <div>
                              <div style={{ fontSize: 14, color: '#065f46', fontWeight: 500 }}>{formatDateTimeTZ(order.assigned_at)}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-success-hover)' }}>Assigned (local time)</div>
                            </div>
                          )}

                          {order.razorpay_payment_id && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.razorpay_payment_id}</span>
                                <button onClick={() => copyToClipboard(order.razorpay_payment_id, 'Payment ID')} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} title="Copy Payment ID">
                                  <Copy size={12} />
                                </button>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Razorpay Payment ID</div>
                            </div>
                          )}

                          {order.razorpay_order_id && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.razorpay_order_id}</span>
                                <button onClick={() => copyToClipboard(order.razorpay_order_id, 'Order ID')} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} title="Copy Razorpay Order ID">
                                  <Copy size={12} />
                                </button>
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Razorpay Order ID</div>
                            </div>
                          )}

                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{order.id.slice(0, 8)}...</span>
                              <button onClick={() => copyToClipboard(order.id, 'Order ID')} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} title="Copy Order ID">
                                <Copy size={12} />
                              </button>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Order ID</div>
                          </div>
                        </div>

                        {order.status === 'failed' && order.failure_reason && (
                          <div style={{ marginTop: 12, padding: 10, backgroundColor: 'var(--color-danger-bg)', borderRadius: 8, border: '1px solid var(--color-danger-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <AlertTriangle size={16} color="#dc2626" />
                              <span style={{ fontWeight: 600, color: 'var(--color-danger-hover)' }}>Payment Failed</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#7f1d1d' }}>{order.failure_reason}</div>
                          </div>
                        )}

                        {order.assigned_at && (
                          <div style={{ marginTop: 12, padding: 10, backgroundColor: '#dcfce7', borderRadius: 8, border: '1px solid var(--color-success-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <CheckCircle2 size={16} color="#059669" />
                              <span style={{ color: '#065f46', fontSize: 13 }}>Assigned by {order.assigned_by} on {formatDateTimeTZ(order.assigned_at)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {copySuccess && (
          <div style={{ position: 'fixed', right: 20, bottom: 20, background: 'var(--color-success)', color: '#fff', padding: '8px 12px', borderRadius: 8 }}>
            <CheckCircle2 size={14} style={{ marginRight: 6 }} />
            {copySuccess} copied
          </div>
        )}

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </main>
    </div>
  );
};

export default MyOrdersPage;